import type { RefObject } from 'preact';
import { useMemo } from 'preact/hooks';

type SelectableHandle = {
  select: () => void;
  setSelectionRange: (start: number, end: number) => void;
};
/**
 * A custom hook that handles the selection or selection range of text in a text
 * input or textarea. Calling select and setSelectionRange will not necessarily
 * focus the input, so it is often used with a focus call immediately beforehand
 * @typedef E represents the type of the selectable element
 */
export function useSelectableTextField<E extends HTMLInputElement | HTMLTextAreaElement>(
  selectElementRef: RefObject<E>
) {
  const methods = useMemo(
    () => ({
      select: () => {
        selectElementRef.current?.select?.();
      },
      setSelectionRange: (start: number, end: number) => {
        // Must be used in conjunction with focus for the call to have any effect
        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange
        selectElementRef.current?.setSelectionRange?.(start, end);
      }
    }),
    [selectElementRef]
  );

  return { methods };
}

export type { SelectableHandle };
